import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { StockService } from '../../services/stock.service';
import { StockLot } from '../../interfaces/stock.interface';
import SearchableSelect from './SearchableSelect';

interface StockLotPickerProps {
    storeId?: string;
    itemId?: string;
    value: string | null;
    onChange: (lotId: string | null) => void;
    disabled?: boolean;
    label?: string;
    /** F6a: grey out expired lots (requisition issue and sale relief refuse them). */
    refuseExpired?: boolean;
    'data-help'?: string;
}

const dash = (value: string | number | null | undefined): string =>
    value === null || value === undefined || value === '' ? '—' : String(value);

const isFabricLot = (lot: StockLot): boolean =>
    [lot.dyeLot, lot.shade, lot.grade, lot.widthCm, lot.gsm].some(v => v !== null && v !== undefined && v !== '');

// Fabric attributes only show for a lot that carries any of them; a dye or lubricant lot reads "Lot 2" / "50 left".
export const lotPrimaryLabel = (lot: StockLot): string =>
    isFabricLot(lot) ? `Lot ${lot.lotNo} · ${dash(lot.dyeLot)} · ${dash(lot.shade)}` : `Lot ${lot.lotNo}`;

export const lotSecondaryLabel = (lot: StockLot): string =>
    isFabricLot(lot)
        ? `${lot.remainingQty} left · Grade ${dash(lot.grade)} · ${dash(lot.widthCm)} cm / ${dash(lot.gsm)} gsm`
        : `${lot.remainingQty} left`;

/** F6a: the lot's expiry, e.g. "Expires 2026-11-01" or "EXPIRED 2026-10-01"; empty for an undated lot. */
export const lotExpiryLabel = (lot: StockLot): string =>
    !lot.expiryDate ? '' : `${lot.expired ? 'EXPIRED' : 'Expires'} ${lot.expiryDate}`;

/**
 * Shade-matched lot picker (inventory-lots close-out, task 5). Loads the available-lot feed for a
 * given store+item via `StockService.getAvailableLots`, disabled until both ids are present.
 * Clearable — clearing the selection means FIFO (server default when `stockLotId` is null), which
 * the helper text surfaces explicitly.
 */
export const StockLotPicker: React.FC<StockLotPickerProps> = ({
    storeId, itemId, value, onChange, disabled = false, label = 'Lot', refuseExpired = false,
    'data-help': dataHelp,
}) => {
    const [lots, setLots] = useState<StockLot[]>([]);
    const [loading, setLoading] = useState(false);

    const enabled = !disabled && !!storeId && !!itemId;

    useEffect(() => {
        if (!enabled || !storeId || !itemId) {
            setLots([]);
            return;
        }
        let cancelled = false;
        setLoading(true);
        StockService.getAvailableLots(storeId, itemId)
            .then(data => {
                if (!cancelled) setLots(data);
            })
            .catch(() => {
                if (!cancelled) setLots([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [enabled, storeId, itemId]);

    return (
        <SearchableSelect<StockLot>
            options={lots}
            value={value}
            onChange={(lotId) => onChange(lotId || null)}
            getOptionLabel={o => (lotExpiryLabel(o) ? `${lotPrimaryLabel(o)} · ${lotExpiryLabel(o)}` : lotPrimaryLabel(o))}
            getOptionDisabled={refuseExpired ? (o => !!o.expired) : undefined}
            disabled={!enabled}
            loading={loading}
            label={label}
            {...(dataHelp ? { ['data-help']: dataHelp } : {})}
            helperText={lots.some(l => l.expiryDate) ? 'Listed earliest expiry first; clear to draw the earliest unexpired lot automatically' : 'Clear to fall back to FIFO'}
            noOptionsText={loading ? 'Loading…' : 'No lots available'}
            renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                    <Box>
                        <Typography variant="body2">{lotPrimaryLabel(option)}</Typography>
                        <Typography variant="caption" color="text.secondary">{lotSecondaryLabel(option)}</Typography>
                        {lotExpiryLabel(option) && (   /* F6a */
                            <Typography variant="caption" display="block" color={option.expired ? 'error' : 'text.secondary'}>
                                {lotExpiryLabel(option)}
                            </Typography>
                        )}
                    </Box>
                </Box>
            )}
        />
    );
};

export default StockLotPicker;
